import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "../../Utils/axiosInstance";
import AppLoader from "../../Components/Common/AppLoader";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import toast from "react-hot-toast";
import { useLockBodyScroll } from "../../Components/Hooks/useLockBodyScroll";
import { useClickOutside } from './../../Components/Hooks/useClickOutside';
import { useAuth } from "../../Components/Context/AuthContext";
import { createPortal } from "react-dom";
import RechargeModal from "../../Components/UserSideComponents/Recharge/RechargeModal";


// ─── ImageModal (ported from OrderDetailCard pattern) ─────────────────────────
const ImageModal = ({ images = [], videos = [], initialIndex = 0, onClose }) => {
  const allMedia = [...images, ...videos];
  const [current, setCurrent] = useState(initialIndex);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    setTimeout(() => setAnimate(true), 10);
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setCurrent((p) => (p + 1) % allMedia.length);
      if (e.key === "ArrowLeft") setCurrent((p) => (p - 1 + allMedia.length) % allMedia.length);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [allMedia.length, onClose]);

  const isVideo = (url) =>
    url?.includes(".mp4") || url?.includes(".webm") || url?.includes("video");

  const currentUrl = allMedia[current];

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm transition-opacity duration-300 ${animate ? "opacity-100" : "opacity-0"}`}
      onClick={onClose}
    >
      <div
        className="relative max-w-4xl w-full mx-4 flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white/70 hover:text-white text-3xl leading-none transition-colors"
        >
          ×
        </button>

        {/* Counter */}
        <div className="absolute -top-10 left-0 text-white/60 text-sm font-medium">
          {current + 1} / {allMedia.length}
        </div>

        {/* Media */}
        <div className="w-full bg-black rounded-xl overflow-hidden flex items-center justify-center" style={{ maxHeight: "75vh" }}>
          {isVideo(currentUrl) ? (
            <video
              src={currentUrl}
              controls
              autoPlay
              className="max-h-[75vh] max-w-full"
            />
          ) : (
            <img
              src={currentUrl}
              alt={`Media ${current + 1}`}
              className="max-h-[75vh] max-w-full object-contain"
            />
          )}
        </div>

        {/* Nav buttons */}
        {allMedia.length > 1 && (
          <>
            <button
              onClick={() => setCurrent((p) => (p - 1 + allMedia.length) % allMedia.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/80 text-white flex items-center justify-center text-xl transition-colors"
            >
              ‹
            </button>
            <button
              onClick={() => setCurrent((p) => (p + 1) % allMedia.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/80 text-white flex items-center justify-center text-xl transition-colors"
            >
              ›
            </button>
          </>
        )}

        {/* Thumbnails */}
        {allMedia.length > 1 && (
          <div className="flex gap-2 mt-4 overflow-x-auto max-w-full pb-1">
            {allMedia.map((url, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${i === current ? "border-white scale-105" : "border-white/20 opacity-60 hover:opacity-100"}`}
              >
                {isVideo(url) ? (
                  <div className="w-full h-full bg-gray-800 flex items-center justify-center text-lg">🎥</div>
                ) : (
                  <img src={url} alt={`Thumb ${i + 1}`} className="w-full h-full object-cover" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── MediaGallery — matches QuotationDetailsContent style exactly ─────────────
const MediaGallery = ({ images = [], videos = [] }) => {
  const allMedia = [...images, ...videos];
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  if (allMedia.length === 0) return null;

  const isVideo = (url) =>
    url?.includes(".mp4") || url?.includes(".webm") || url?.includes("video");

  const downloadMedia = async (url, idx) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const extension = blob.type.split("/")[1]?.split("+")[0] || "jpg";
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `reference-media-${idx + 1}.${extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Download failed. Opening in new tab instead.");
      window.open(url, "_blank");
    }
  };

  const downloadAllMedia = async () => {
    const toastId = "dl-all-ref";
    toast.loading(`Preparing zip for ${allMedia.length} files...`, { id: toastId });
    try {
      const zip = new JSZip();
      const folder = zip.folder("reference-media");
      await Promise.all(
        allMedia.map(async (url, idx) => {
          try {
            const response = await fetch(url);
            const blob = await response.blob();
            const extension = blob.type.split("/")[1]?.split("+")[0] || "jpg";
            folder.file(`reference-media-${idx + 1}.${extension}`, blob);
          } catch (err) {
            console.error(`Failed to fetch media ${idx + 1}:`, err);
          }
        })
      );
      toast.loading("Zipping files...", { id: toastId });
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, `reference-media-${Date.now()}.zip`);
      toast.dismiss(toastId);
      toast.success(`Downloaded ${allMedia.length} files as zip ✅`);
    } catch (err) {
      toast.dismiss(toastId);
      toast.error("Zip creation failed. Try downloading individually.");
    }
  };

  return (
    <SectionCard>
      {/* Header row with Download All */}
      <div className="flex items-center justify-between mb-4">
        <SectionTitle icon="🖼️">Photos &amp; videos</SectionTitle>
        <button
          onClick={downloadAllMedia}
          className="flex items-center gap-1.5 text-xs font-bold text-primaryColor hover:text-primaryColor/80 transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 4v11" />
          </svg>
          Download All
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        {allMedia.map((url, i) => (
          <div key={i} className="group relative w-24 h-24 sm:w-28 sm:h-28">
            {/* View / play button */}
            <button
              onClick={() => {
                setModalIndex(i);
                setModalOpen(true);
              }}
              className="w-full h-full rounded-xl overflow-hidden border border-gray-200 hover:border-primaryColor/40 transition-all hover:shadow-md hover:scale-[1.02] bg-gray-100 block"
            >
              {isVideo(url) ? (
                <div className="w-full h-full flex items-center justify-center bg-gray-900">
                  <span className="text-3xl">🎥</span>
                </div>
              ) : (
                <img
                  src={url}
                  alt={`Media ${i + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-xl" />
              <div className="absolute bottom-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded-md font-medium">
                  {isVideo(url) ? "Play" : "View"}
                </span>
              </div>
            </button>

            {/* Per-item download */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                downloadMedia(url, i);
              }}
              title="Download"
              className="absolute top-1.5 right-1.5 z-10 w-6 h-6 rounded-md bg-black/60 hover:bg-primaryColor flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200"
            >
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 4v11" />
              </svg>
            </button>
          </div>
        ))}
      </div>

      {/* Image modal */}
      {modalOpen && (
        <ImageModal
          images={images}
          videos={videos}
          initialIndex={modalIndex}
          onClose={() => setModalOpen(false)}
        />
      )}
    </SectionCard>
  );
};

// ─── PlaceOrderModal ───────────────────────────────────────────────────────────
const PlaceOrderModal = ({
  show,
  onClose,
  offerId,
  quotationId,
  platformFeeAmount,
  onSuccess,
}) => {
  const [animate, setAnimate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth()

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [buyerDecision, setBuyerDecision] = useState("Proceed_With_Sample");
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [addressLoading, setAddressLoading] = useState(false);
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const domNode = useClickOutside(() => handleClose());

  useEffect(() => {
    if (show) {
      setTimeout(() => setAnimate(true), 10);
      fetchAddresses();
      setDeclarationAccepted(false);
    } else {
      setAnimate(false);
    }
  }, [show]);

  const fetchAddresses = async () => {
    try {
      setAddressLoading(true);
      const res = await axiosInstance.get(`${process.env.REACT_APP_API_URL}/address`);
      const data = res?.data?.data || [];
      setAddresses(data);
      const def = data.find((a) => a.isDefault);
      if (def) setSelectedAddressId(def._id);
    } catch (err) {
      console.error("Failed to load addresses", err);
    } finally {
      setAddressLoading(false);
    }
  };

  const handleClose = () => {
    setAnimate(false);
    setTimeout(onClose, 250);
  };

  const handleSubmit = async () => {
    if (!fromDate) return alert("Please select a delivery start date");
    if (!toDate) return alert("Please select a delivery end date");
    if (new Date(toDate) <= new Date(fromDate))
      return alert("End date must be after start date");
    if (!selectedAddressId) return alert("Please select a delivery address");

    if (!declarationAccepted) {
      return toast.error(
        "Please accept the delivery time declaration before proceeding."
      );
    }
    try {
      setSubmitting(true);

      const orderRes = await axiosInstance.post(
        `/offer/create-order-via-offer`,
        {
          offerId,
          quotationId,
          amount: platformFeeAmount,
          addressId: selectedAddressId,
          expectedDeliveryTime: { fromDate, toDate },
          buyerDecision,
        }
      );

      const orderId = orderRes?.data?.data?.orderId;

      await axiosInstance.post("/declaration", {
        userId: user.id,
        reason: "Expected Delivery Time Declaration",
        declaration_is_for: "Expected_Delivery_Time",
        declaration_contents: [
          {
            content:
              "The expected delivery time might differ depending on the transport service..",
          },
        ],
        orderId,
        e_sign: user.e_signature || "N/A",
        date: new Date(),
      });
      handleClose();
      onSuccess();
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to place order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  const today = new Date().toISOString().split("T")[0];

  return (
    createPortal(
      <div
        className={`fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${animate ? "opacity-100" : "opacity-0"}`}
      >
        <div
          ref={domNode}
          className={`bg-white w-full md:w-[500px] md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden ${animate ? "scale-100 translate-y-0" : "scale-95 md:translate-y-4 translate-y-full"}`}
          style={{ maxHeight: "92vh" }}
        >
          {/* Header */}
          <div className="px-5 py-4 bg-primaryColor text-white flex items-center gap-3 flex-shrink-0">
            <button onClick={handleClose} className="text-2xl leading-none">←</button>
            <div className="flex-1">
              <p className="font-semibold text-base">Place Order</p>
              <p className="text-xs text-white/70">Fill in a few details to confirm</p>
            </div>
          </div>

          {/* Body */}
          <div className="p-5 space-y-5 overflow-y-auto flex-1">
            {/* Delivery Date Range */}
            <div>
              <p className="text-xs font-semibold text-primaryColor uppercase tracking-widest mb-3">
                Expected Delivery Window
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-secondaryTextColor block mb-1.5">
                    From date <span className="text-primaryColor">*</span>
                  </label>
                  <input
                    type="date"
                    min={today}
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-primaryTextColor outline-none focus:border-primaryColor focus:ring-2 focus:ring-primaryColor/10 transition-all font-Urbanist"
                  />
                </div>
                <div>
                  <label className="text-xs text-secondaryTextColor block mb-1.5">
                    To date <span className="text-primaryColor">*</span>
                  </label>
                  <input
                    type="date"
                    min={fromDate || today}
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-primaryTextColor outline-none focus:border-primaryColor focus:ring-2 focus:ring-primaryColor/10 transition-all font-Urbanist"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={declarationAccepted}
                  onChange={(e) => setDeclarationAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-primaryColor"
                />

                <div>
                  <p className="text-sm font-semibold text-amber-800">
                    Delivery Time Declaration
                  </p>

                  <p className="text-xs text-amber-700 leading-relaxed mt-1">
                    The expected delivery time might differ depending on the transport service..
                  </p>
                </div>
              </label>
            </div>

            {/* Buyer Decision */}
            <div>
              <p className="text-xs font-semibold text-primaryColor uppercase tracking-widest mb-3">
                How do you want to proceed?
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: "Proceed_With_Sample", label: "Sample First", icon: "🧪", desc: "Receive a sample before committing to full order" },
                  { value: "Proceed_Full_Order", label: "Full Order", icon: "📦", desc: "Skip sample and go directly to full order" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setBuyerDecision(opt.value)}
                    className={`text-left p-4 rounded-2xl border-2 transition-all ${buyerDecision === opt.value ? "border-primaryColor bg-[#fef6f8]" : "border-gray-200 bg-white hover:border-gray-300"}`}
                  >
                    <div className="text-xl mb-1.5">{opt.icon}</div>
                    <p className={`text-sm font-bold mb-1 ${buyerDecision === opt.value ? "text-primaryColor" : "text-primaryTextColor"}`}>
                      {opt.label}
                    </p>
                    <p className="text-[11px] text-secondaryTextColor leading-relaxed">{opt.desc}</p>
                    {buyerDecision === opt.value && (
                      <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-primaryColor bg-primaryColor/10 px-2 py-0.5 rounded-full">
                        ✓ Selected
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Address */}
            <div>
              <p className="text-xs font-semibold text-primaryColor uppercase tracking-widest mb-3">
                Delivery Address <span className="text-primaryColor">*</span>
              </p>
              {addressLoading ? (
                <div className="space-y-2">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-16 rounded-xl bg-gray-100 animate-pulse" />
                  ))}
                </div>
              ) : addresses.length === 0 ? (
                <div className="text-center py-6 border-2 border-dashed border-gray-200 rounded-xl">
                  <p className="text-sm text-secondaryTextColor">No addresses found.</p>
                  <p className="text-xs text-gray-400 mt-1">Please add an address from your profile.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {addresses.map((addr) => (
                    <button
                      key={addr._id}
                      type="button"
                      onClick={() => setSelectedAddressId(addr._id)}
                      className={`w-full text-left p-3.5 rounded-xl border-2 transition-all ${selectedAddressId === addr._id ? "border-primaryColor bg-[#fef6f8]" : "border-gray-200 bg-white hover:border-gray-300"}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedAddressId === addr._id ? "border-primaryColor" : "border-gray-300"}`}>
                          {selectedAddressId === addr._id && (
                            <div className="w-2 h-2 rounded-full bg-primaryColor" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-primaryTextColor truncate">{addr.addressLine1}</p>
                            {addr.isDefault && (
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-primaryColor/10 text-primaryColor flex-shrink-0">
                                Default
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-secondaryTextColor mt-0.5">
                            {[addr.city, addr.state, addr.pincode].filter(Boolean).join(", ")}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-5 border-t flex-shrink-0 space-y-3">
            <button
              onClick={handleSubmit}
              disabled={submitting || !declarationAccepted}
              className="w-full bg-primaryColor hover:bg-primaryBtnHoverColor text-white py-3.5 rounded-xl font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Placing order…
                </>
              ) : (
                "Confirm & Place Order →"
              )}
            </button>
            <button
              onClick={handleClose}
              disabled={submitting}
              className="w-full py-3 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>, document.body
    )
  );
};

// ─── PayPlatformFeeConfirmModal ───────────────────────────────────────────────
const PayPlatformFeeConfirmModal = ({
  show,
  onClose,
  platformFee,
  wallet,
  onConfirmPay,
  onAddMoney,
  submitting,
}) => {
  const [animate, setAnimate] = useState(false);
  const domNode = useClickOutside(() => handleClose());

  useEffect(() => {
    if (show) {
      setTimeout(() => setAnimate(true), 10);
    } else {
      setAnimate(false);
    }
  }, [show]);

  const handleClose = () => {
    setAnimate(false);
    setTimeout(onClose, 250);
  };

  if (!show) return null;

  const availableBalance = wallet?.availableBalance ?? 0;
  const isSufficient = availableBalance >= platformFee;
  const remainingBalance = Math.max(0, availableBalance - platformFee);

  return createPortal(
    <div
      onClick={handleClose}
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs transition-opacity duration-300 ${animate ? "opacity-100" : "opacity-0"
        }`}
    >
      <div
        ref={domNode}
        onClick={(e) => e.stopPropagation()}
        className={`bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden transition-all duration-300 ${animate ? "scale-100 translate-y-0" : "scale-95 translate-y-4"
          }`}
      >
        {/* Modal Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-[#3b2012] to-[#5a3020] text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="text-xl">💳</span>
            <div>
              <h3 className="font-bold text-base leading-tight">
                Unlock Offer Details
              </h3>
              <p className="text-[11px] text-white/70">
                Platform fee payment confirmation
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-all cursor-pointer border border-white/20 active:scale-95"
            title="Close"
          >
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-4">
          <p className="text-xs text-gray-600 leading-relaxed">
            Pay a one-time platform fee of{" "}
            <strong className="text-gray-900 font-bold">₹{platformFee}</strong> to unlock price breakdown details and enable order placement for this offer.
          </p>

          {/* Wallet Summary Card */}
          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4 space-y-2.5">
            <div className="flex justify-between items-center text-xs">
              <span className="text-gray-500">Available Wallet Balance</span>
              <span className="font-bold text-gray-800">
                ₹{availableBalance.toLocaleString("en-IN")}
              </span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-gray-500">Platform Fee</span>
              <span className="font-bold text-red-600">
                - ₹{platformFee}
              </span>
            </div>
            <hr className="border-t border-gray-200" />
            <div className="flex justify-between items-center text-xs">
              <span className="font-semibold text-gray-700">
                Est. Balance After Payment
              </span>
              <span
                className={`font-bold ${isSufficient ? "text-green-700" : "text-amber-600"
                  }`}
              >
                ₹{remainingBalance.toLocaleString("en-IN")}
              </span>
            </div>
          </div>

          {/* Insufficient balance warning if applicable */}
          {!isSufficient && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5 flex items-start gap-2.5">
              <span className="text-amber-500 text-sm mt-0.5">⚠️</span>
              <p className="text-xs text-amber-800 leading-relaxed font-medium">
                Your wallet balance is insufficient by{" "}
                <strong className="font-bold">
                  ₹{(platformFee - availableBalance).toLocaleString("en-IN")}
                </strong>
                . Click below to add money to your wallet.
              </p>
            </div>
          )}
        </div>

        {/* Modal Footer / Actions */}
        <div className="p-5 border-t border-gray-100 flex items-center gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 py-3 border border-gray-200 rounded-xl text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          {isSufficient ? (
            <button
              type="button"
              onClick={onConfirmPay}
              disabled={submitting}
              className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold transition-all shadow-md flex items-center justify-center gap-1.5 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Paying...
                </>
              ) : (
                <>
                  <span>Confirm &amp; Pay ₹{platformFee}</span>
                  <span>→</span>
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={onAddMoney}
              className="flex-1 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold transition-all shadow-md flex items-center justify-center gap-1.5"
            >
              <span>Add Money &amp; Pay</span>
              <span>→</span>
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── PriceBreakdownSection ─────────────────────────────────────────────────────
const PriceBreakdownSection = ({ priceBreakdown, standardUnit, packsizeunit }) => {
  const [activeTab, setActiveTab] = useState("sample");

  if (!priceBreakdown) return null;

  const sampleBreakdown = priceBreakdown?.sample?.breakdown ?? null;
  const fullOrderBreakdown = priceBreakdown?.fullOrder?.breakdown ?? null;
  // console.log("price breakdown in the post offer", priceBreakdown)
  // console.log("sample breakdown in the post offer", sampleBreakdown)
  // console.log("full order breakdown in the post offer", fullOrderBreakdown)
  // const packsizeunit = data?.quotation?.packSize.unit || data?.quotation?.standardizedUnit || "";

  if (!sampleBreakdown && !fullOrderBreakdown) return null;

  const tabs = [
    ...(sampleBreakdown ? [{ key: "sample", label: "Sample", icon: "🧪" }] : []),
    ...(fullOrderBreakdown ? [{ key: "fullOrder", label: "Full Order", icon: "📦" }] : []),
  ];

  const activeBreakdown = activeTab === "sample" ? sampleBreakdown : fullOrderBreakdown;

  const roundINR = (n) =>
    n !== undefined && n !== null ? Math.round(n).toLocaleString("en-IN") : "—";

  const rows = activeBreakdown
    ? [
      // { label: "Minimum Pack Weight", value: `${Math.round(activeBreakdown.packSizeWeight)} ${packsizeunit}` },
      // { label: "No. of bags/boxes/packs", value: activeBreakdown.quantity },
      { label: "Offer Quantity", value: `${Math.round(activeBreakdown.totalWeightKg)} ${standardUnit}` },
      // { label: `Price per ${standardUnit}`, value: `₹${roundINR(activeBreakdown.pricePerKg)}` },
      { label: "Base amount", value: `₹${roundINR(activeBreakdown.baseAmount)}` },
      { label: `GST (${activeBreakdown.gstRate}%)`, value: `₹${roundINR(activeBreakdown.gstAmount)}` },
      {
        label: "Transportation charges",
        value: activeBreakdown.transportationCharges > 0
          ? `₹${roundINR(activeBreakdown.transportationCharges)}`
          : "To be determined",
        muted: activeBreakdown.transportationCharges === 0,
      },
    ]
    : [];

  return (
    <SectionCard>
      <SectionTitle icon="🧾">Price breakdown</SectionTitle>

      {tabs.length > 1 && (
        <div className="flex gap-2 mb-5">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${activeTab === tab.key ? "bg-[#772037] text-white shadow-sm" : "bg-gray-100 text-[#6E6E6E] hover:bg-gray-200"}`}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#fce8ed] text-[#772037]">
          {activeBreakdown?.currency ?? "INR"}
        </span>
        <span className="text-xs text-[#6E6E6E]">All amounts in Indian Rupees</span>
      </div>

      <div className="rounded-2xl border border-gray-100 overflow-hidden mb-4">
        {rows.map((row, i) => (
          <div
            key={i}
            className={`flex items-center justify-between px-4 py-3 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/70"} ${i !== rows.length - 1 ? "border-b border-gray-100" : ""}`}
          >
            <span className="text-sm text-[#6E6E6E]">{row.label}</span>
            <span className={`text-sm font-semibold ${row.muted ? "text-[#6E6E6E] italic" : "text-[#191919]"}`}>
              {row.value}
            </span>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between bg-[#772037] rounded-2xl px-5 py-4 mb-3">
        <div>
          <p className="text-white/70 text-xs mb-0.5">Estimated total</p>
          <p className="text-white font-bold text-xl">
            ₹{roundINR(activeBreakdown?.totalAmount)}
          </p>
        </div>
        <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-xl">🧮</div>
      </div>

      <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
        <span className="text-amber-500 text-base flex-shrink-0 mt-0.5">⚠️</span>
        <p className="text-xs text-amber-800 leading-relaxed">
          The total amount shown is an estimate. Final amount may vary based on transportation charges,
          which depend on your delivery address and will be confirmed after order placement.
        </p>
      </div>
    </SectionCard>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const PostOrderViaOffer = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const offerId = searchParams.get("offerId");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [hasPaid, setHasPaid] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(true);
  const [payingPlatformFee, setPayingPlatformFee] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [showRecharge, setShowRecharge] = useState(false);
  const [showPayConfirmModal, setShowPayConfirmModal] = useState(false);
  useLockBodyScroll(showModal || showRecharge || showPayConfirmModal);

  const PLATFORM_FEE = 69;
  const standardUnit = data?.quotation?.standardizedUnit ?? "";
  const packsizeunit = data?.quotation?.packSize?.unit || "";

  useEffect(() => {
    if (!offerId) {
      setError("No offer ID provided.");
      setLoading(false);
      return;
    }
    fetchOfferDetails();
    checkPaymentStatus();
    fetchWallet();
  }, [offerId]);

  const checkPaymentStatus = async () => {
    try {
      setCheckingPayment(true);
      const res = await axiosInstance.get(`/offer/check-payment-status/${offerId}`);
      if (res.data?.success) {
        setHasPaid(Boolean(res.data.paid));
      }
    } catch (err) {
      console.error("Failed to check offer payment status:", err);
    } finally {
      setCheckingPayment(false);
    }
  };

  const fetchOfferDetails = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/offer/${offerId}/quotation-details`
      );
      setData(res.data.data);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load offer details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchWallet = async () => {
    try {
      setWalletLoading(true);
      const userDataStr = localStorage.getItem("qqc_userData");
      const userData = userDataStr ? JSON.parse(userDataStr) : null;
      const userId = userData?._id || userData?.id;
      if (!userId) return;
      const res = await axiosInstance.get(`/wallet/get-wallet-balance/${userId}`);
      setWallet(res.data?.data);
    } catch (err) {
      console.error("Failed to fetch wallet balance:", err);
    } finally {
      setWalletLoading(false);
    }
  };

  const handleOpenPayConfirmModal = () => {
    fetchWallet();
    setShowPayConfirmModal(true);
  };

  const handleConfirmPayPlatformFee = async () => {
    try {
      setPayingPlatformFee(true);
      setShowPayConfirmModal(false);
      const res = await axiosInstance.post(`/offer/pay-to-view/${offerId}`, {
        amount: PLATFORM_FEE,
      });
      if (res.data?.success) {
        toast.success("₹69 platform fee paid successfully! Order section unlocked.");
        setHasPaid(true);
        fetchWallet();
      } else {
        toast.error(res.data?.message || "Failed to pay platform fee.");
      }
    } catch (err) {
      console.error("Payment error:", err);
      const msg = err?.response?.data?.message || "Payment failed. Please check your wallet balance.";
      if (msg.toLowerCase().includes("balance") || msg.toLowerCase().includes("insufficient")) {
        setShowPayConfirmModal(true);
      } else {
        toast.error(msg);
      }
    } finally {
      setPayingPlatformFee(false);
    }
  };

  const handleAddMoneyAndPay = () => {
    setShowPayConfirmModal(false);
    setShowRecharge(true);
  };

  const handleCopyShareLink = () => {
    const shareUrl = `${window.location.origin}/post-order-via-offer?offerId=${offerId}`;
    navigator.clipboard.writeText(shareUrl).then(
      () => {
        setCopiedLink(true);
        toast.success("Shareable offer link copied to clipboard!");
        setTimeout(() => setCopiedLink(false), 3000);
      },
      () => {
        toast.error("Failed to copy link.");
      }
    );
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  const handleProceed = () => {
    if (!hasPaid) {
      handleOpenPayConfirmModal();
      return;
    }
    setShowModal(true);
  };

  if (loading || checkingPayment) {
    return (
      <div className="min-h-screen bg-gray-50 font-Urbanist flex items-center justify-center">
        <AppLoader message="Loading offer details…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 font-Urbanist flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-lg font-semibold text-primaryTextColor mb-2">Something went wrong</p>
          <p className="text-sm text-secondaryTextColor mb-4">{error}</p>
          <button onClick={() => navigate(-1)} className="text-sm font-semibold text-primaryColor underline">
            Go back
          </button>
        </div>
      </div>
    );
  }

  if (orderSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 font-Urbanist flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center text-4xl mx-auto mb-4">🎉</div>
          <p className="text-2xl font-bold text-primaryColor mb-2">Order Placed!</p>
          <p className="text-sm text-secondaryTextColor mb-6">Your order has been successfully placed.</p>
          <button
            onClick={() => navigate("/orders")}
            className="px-6 py-3 bg-primaryColor text-white rounded-xl font-semibold text-sm hover:bg-primaryBtnHoverColor transition"
          >
            View My Orders
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { productHierarchy, quotation, offer } = data;
  // console.log("quotation in the Post oder via offer", quotation);
  const pv = productHierarchy?.productVariety ?? {};
  const product = productHierarchy?.product ?? {};
  const category = productHierarchy?.category ?? {};
  const subCategory = productHierarchy?.subCategory ?? {};
  const questions = quotation?.specification?.relatedQuestionsAndAnswers ?? [];
  const otherReqs = quotation?.specification?.otherRequirements ?? [];

  return (
    <div className="min-h-screen pb-16 relative bg-gray-50 font-Urbanist">
      <PlaceOrderModal
        show={showModal}
        onClose={() => setShowModal(false)}
        offerId={offerId}
        quotationId={quotation?._id}
        platformFeeAmount={PLATFORM_FEE}
        onSuccess={() => {
          setShowModal(false);
          setOrderSuccess(true);
        }}
      />

      <RechargeModal
        isOpen={showRecharge}
        onClose={() => setShowRecharge(false)}
        wallet={wallet}
        user={(() => {
          try {
            return JSON.parse(localStorage.getItem("qqc_userData") || "{}");
          } catch {
            return {};
          }
        })()}
        onSuccess={() => {
          setShowRecharge(false);
          fetchWallet();
          setShowPayConfirmModal(true);
        }}
      />

      <PayPlatformFeeConfirmModal
        show={showPayConfirmModal}
        onClose={() => setShowPayConfirmModal(false)}
        platformFee={PLATFORM_FEE}
        wallet={wallet}
        onConfirmPay={handleConfirmPayPlatformFee}
        onAddMoney={handleAddMoneyAndPay}
        submitting={payingPlatformFee}
      />

      {/* Page Header */}
      <div className="bg-white border-b border-gray-100 px-6 py-4">
        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-2">
          <button onClick={() => navigate(-1)} className="hover:text-primaryColor transition-colors">
            Offers
          </button>
          <span>›</span>
          <span className="text-primaryColor font-medium">Post Order via Offer</span>
        </div>

        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primaryTextColor">Review &amp; Post Order</h1>
            <p className="text-sm text-secondaryTextColor mt-0.5">
              Go through the offer quotation details and fill in your requirements below.
            </p>

            <div className="flex items-center gap-2 mt-2.5 flex-wrap">
              {offer?.offerUniqueNumber && (
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-primaryColor bg-primaryColor/10 border border-primaryColor/20 px-3 py-1 rounded-lg">
                  <span className="w-1.5 h-1.5 rounded-full bg-primaryColor animate-pulse" />
                  Offer #{offer.offerUniqueNumber}
                </span>
              )}
              {quotation?.quotationUniqueNumber && (
                <span className="text-xs font-semibold text-secondaryTextColor bg-gray-100 px-3 py-1 rounded-lg">
                  Quotation #{quotation.quotationUniqueNumber}
                </span>
              )}
            </div>
          </div>
          {hasPaid ? (
            <button
              onClick={handleProceed}
              className="w-fit px-5 py-3 bg-primaryColor hover:bg-primaryBtnHoverColor text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm"
            >
              Proceed to place order →
            </button>
          ) : (
            <button
              onClick={handleOpenPayConfirmModal}
              disabled={payingPlatformFee}
              className="w-fit px-5 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all shadow-md active:scale-95"
            >
              {payingPlatformFee ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <span>Pay ₹{PLATFORM_FEE} Platform Fee to Order</span>
                  <span>→</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-0 min-h-[calc(100vh-80px)]">
        {/* LEFT COLUMN */}
        <div className="overflow-y-auto px-6 py-6 space-y-4 border-r border-gray-100">
          {/* Product Details */}
          <SectionCard>
            <SectionTitle icon="🌿">Product details</SectionTitle>
            <div className="flex gap-4 items-start mb-4">
              <div className="w-16 h-16 rounded-xl border border-gray-100 bg-gray-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {pv.img ? (
                  <img src={pv.img} alt={pv.varietyName} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-2xl">🌶️</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-lg font-bold text-primaryTextColor leading-snug">{pv.varietyName ?? "—"}</div>
                  {/* Share Link Button */}
                  <button
                    onClick={handleCopyShareLink}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50 hover:border-primaryColor/30 transition-all shadow-2xs flex-shrink-0"
                  >
                    {copiedLink ? (
                      <>
                        <span className="text-green-600 font-bold">✓</span>
                        <span className="text-green-600 font-bold">Link Copied!</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 100-5.999 3 3 0 000 5.999zm0 11.998a3 3 0 100-5.999 3 3 0 000 5.999z" />
                        </svg>
                        <span>Copy Shareable Link</span>
                      </>
                    )}
                  </button>
                </div>
                <div className="text-sm text-secondaryTextColor mt-0.5">
                  Grade: {pv.grade ?? "—"} &nbsp;·&nbsp; {product.name ?? "—"} &nbsp;·&nbsp;
                  {category.name ?? "—"} &gt; {subCategory.name ?? "—"}
                </div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#EAF3DE] text-[#3B6D11]">
                    ✓ Approved
                  </span>
                </div>
              </div>
            </div>
            <Divider />
          </SectionCard>

          {/* Quotation Overview */}
          <SectionCard>
            <SectionTitle icon="📄">Quotation overview</SectionTitle>
            <div className="flex items-center justify-between bg-[#fef6f8] border border-primaryColor/10 rounded-xl px-4 py-3 mb-4">
              <span className="text-sm text-secondaryTextColor">Offered price</span>
              <span className="text-2xl font-bold text-primaryColor">
                ₹{quotation.pricing?.commissionAdjustedAmount != null ? Math.round(quotation.pricing.commissionAdjustedAmount) : "—"}
                <span className="text-sm font-normal text-secondaryTextColor ml-1">/ {standardUnit}</span>
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              <Field
                label="Offer Quantity"
                value={quotation.quantity?.volume != null ? `${quotation.quantity.volume.toLocaleString()} ${standardUnit}` : "—"}
              />
              <div>
                <label className="text-xs text-secondaryTextColor block mb-1">Full qty available</label>
                <span className={`inline-flex items-center text-[12px] font-semibold px-2.5 py-1 rounded-full ${quotation.quantity?.isAvailableFull ? "bg-[#EAF3DE] text-[#3B6D11]" : "bg-[#FCEBEB] text-[#A32D2D]"}`}>
                  {quotation.quantity?.isAvailableFull ? "Yes" : "No"}
                </span>
              </div>
              <Field label="Batch / lot no." value={quotation.batch_Lot_Number} />
              <Field label="Currency" value={quotation.currency} />
              <Field label="Holding from" value={formatDate(quotation.holdingPeriod?.from)} />
              <Field label="Holding to" value={formatDate(quotation.holdingPeriod?.to)} />
              {quotation.packSize && (
                <>
                  <Field
                    label="Minimum Pack Weight"
                    value={quotation.packSize.weight ? `${quotation.packSize.weight} ${packsizeunit} ` : "—"}
                  />
                  <Field
                    label="Dimensions (L × B × H)"
                    value={
                      quotation.packSize.dimensions
                        ? `${quotation.packSize.dimensions.length} × ${quotation.packSize.dimensions.breadth} × ${quotation.packSize.dimensions.height} ${quotation.packSize.dimensions.measured}`
                        : "—"
                    }
                  />
                </>
              )}
            </div>
          </SectionCard>

          {/* ── Photos & Videos — now with ImageModal + download buttons ── */}
          <MediaGallery
            images={quotation.media?.images ?? []}
            videos={quotation.media?.videos ?? []}
          />

          {/* Offer Commitments */}
          {otherReqs.length > 0 && (
            <SectionCard>
              <SectionTitle icon="📋">Offer commitments</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {otherReqs.map((req, i) => (
                  <div key={req._id ?? i} className="bg-gray-50 rounded-xl p-3.5">
                    <div className="flex justify-between items-start gap-2">
                      <span className="text-sm text-primaryTextColor">{req.buyersRequirement}</span>
                      <AgreedBadge val={req.supplierAction?.isAgreed} />
                    </div>
                    {req.supplierAction?.thoughts && (
                      <p className="text-xs text-secondaryTextColor mt-1.5">{req.supplierAction.thoughts}</p>
                    )}
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {/* Admin Review Note */}
          {quotation.review?.reviewNote && (
            <div className="rounded-2xl border border-primaryColor/20 bg-[#fef9fa] p-5">
              <div className="flex gap-3 items-start">
                <span className="text-xl flex-shrink-0 mt-0.5">🛡️</span>
                <div>
                  <p className="text-sm font-semibold text-primaryColor mb-1">Review note from admin</p>
                  <p className="text-sm text-secondaryTextColor">{quotation.review.reviewNote}</p>
                  {quotation.review.reviewedBy?.name && (
                    <p className="text-[11px] text-secondaryTextColor mt-2">
                      Reviewed by {quotation.review.reviewedBy.name} &nbsp;·&nbsp; {formatDate(quotation.review.reviewedAt)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN */}
        <div className="bg-white relative xl:sticky xl:top-0 xl:h-screen xl:overflow-y-auto flex flex-col px-6 py-6 gap-4">
          {/* Q&A */}
          {questions.length > 0 && (
            <SectionCard>
              <SectionTitle icon="❓">Specification Q&amp;A</SectionTitle>
              <p className="text-sm text-secondaryTextColor mb-4">
                Buyer's answers and supplier's responses to specification questions.
              </p>
              <div className="flex flex-col gap-3">
                {questions.map((q, i) => (
                  <div key={q._id ?? i} className="bg-gray-50 rounded-xl p-4">
                    <div className="flex gap-2.5 items-start mb-3">
                      <span className="min-w-[22px] h-[22px] bg-primaryColor text-white rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <span className="text-sm font-semibold text-primaryTextColor">{q.question}</span>
                    </div>
                    <div className="mb-3 pl-1">
                      <p className="text-[11px] text-secondaryTextColor uppercase tracking-wider font-semibold mb-1">
                        Buyer's answer
                      </p>
                      <p className="text-sm text-primaryTextColor bg-white border border-gray-200 rounded-lg px-3 py-2">
                        {q.answeredByBuyer || "—"}
                      </p>
                    </div>
                    {q.supplierAction && (
                      <div className="pl-1">
                        <p className="text-[11px] text-secondaryTextColor uppercase tracking-wider font-semibold mb-1.5">
                          Supplier's response
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <AgreedBadge val={q.supplierAction.isAgreed} />
                          {q.supplierAction.thoughts && (
                            <p className="text-xs text-secondaryTextColor">{q.supplierAction.thoughts}</p>
                          )}
                        </div>
                        {q.supplierAction.proof?.length > 0 && (
                          <div className="flex gap-2 mt-2 flex-wrap">
                            {q.supplierAction.proof.map((url, pi) => (
                              <a key={pi} href={url} target="_blank" rel="noreferrer" className="text-[11px] font-semibold text-primaryColor underline">
                                Proof {pi + 1}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {/* Price Breakdown Section (Blurred if unpaid) */}
          {hasPaid ? (
            <PriceBreakdownSection priceBreakdown={data?.priceBreakdown} standardUnit={standardUnit} packsizeunit={packsizeunit} />
          ) : (
            <div className="relative rounded-2xl overflow-hidden border border-red-200 shadow-sm bg-white my-1">
              <div className="filter blur-sm select-none pointer-events-none opacity-40">
                <PriceBreakdownSection priceBreakdown={data?.priceBreakdown} standardUnit={standardUnit} packsizeunit={packsizeunit} />
              </div>
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-6 bg-gradient-to-b from-red-950/10 via-red-950/20 to-red-950/35 backdrop-blur-[2px] text-center">
                <div className="w-12 h-12 rounded-full bg-red-600 text-white flex items-center justify-center text-xl mb-3 shadow-lg shadow-red-500/30">
                  🔒
                </div>
                <h4 className="text-base font-bold text-red-950 mb-1">
                  Pay ₹{PLATFORM_FEE} platform fee to unlock this section
                </h4>
                <p className="text-xs text-red-950/90 max-w-sm mb-4 leading-relaxed font-medium">
                  Unlock detailed price breakdown, unit costs, GST calculation, and enable order placement.
                </p>
                <button
                  onClick={handleOpenPayConfirmModal}
                  disabled={payingPlatformFee}
                  className="px-6 py-2.5 bg-red-600 hover:bg-red-700 active:scale-95 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center gap-2"
                >
                  {payingPlatformFee ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Processing Payment...
                    </>
                  ) : (
                    <>
                      <span>Pay ₹{PLATFORM_FEE} Platform Fee</span>
                      <span>→</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Order Charges */}
          {hasPaid ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <p className="text-xs font-semibold text-primaryColor uppercase tracking-widest mb-3">
                Order charges
              </p>
              <div className="flex justify-between items-center bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-3">
                <div className="flex items-center gap-2 text-sm text-green-700 font-semibold">
                  <span>✓</span>
                  Platform fee
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 ml-1">
                    Already Paid
                  </span>
                </div>
                <span className="text-lg font-bold text-green-700">₹{PLATFORM_FEE}</span>
              </div>
              <p className="text-xs text-secondaryTextColor">
                You've already unlocked this offer's details. No additional platform fee is
                charged when placing your order.
              </p>
            </div>
          ) : (
            <div className="rounded-2xl border border-red-200 bg-red-50/50 p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-red-900 uppercase tracking-widest">
                  Order charges
                </p>
                <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300/50">
                  Payment Required
                </span>
              </div>
              <div className="flex justify-between items-center bg-white border border-red-200 rounded-xl px-4 py-3 mb-3 shadow-2xs">
                <div>
                  <span className="text-sm font-semibold text-gray-800 block">Platform Fee</span>
                  <span className="text-[11px] text-gray-500">Pay ₹{PLATFORM_FEE} to unlock full order details</span>
                </div>
                <span className="text-lg font-extrabold text-red-700">₹{PLATFORM_FEE}</span>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                Pay ₹{PLATFORM_FEE} platform fee to unlock the price breakdown section and place your order.
              </p>
              {/* <button
                onClick={handleOpenPayConfirmModal}
                disabled={payingPlatformFee}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-xl font-bold text-xs transition-all shadow-md active:scale-[0.99] flex items-center justify-center gap-2"
              >
                {payingPlatformFee ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing Payment...
                  </>
                ) : (
                  <>
                    <span>Pay ₹{PLATFORM_FEE} Platform Fee</span>
                    <span>→</span>
                  </>
                )}
              </button> */}
            </div>
          )}

          {/* Action Button */}
          {hasPaid ? (
            <button
              onClick={handleProceed}
              className="w-full p-4 bg-primaryColor hover:bg-primaryBtnHoverColor text-white rounded-xl py-3.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm"
            >
              Proceed to place order →
            </button>
          ) : (
            <button
              onClick={handleOpenPayConfirmModal}
              disabled={payingPlatformFee}
              className="w-full py-3.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all shadow-md active:scale-95"
            >
              {payingPlatformFee ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <span>Pay ₹{PLATFORM_FEE} Platform Fee to Place Order</span>
                  <span>→</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────────
const SectionCard = ({ children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">{children}</div>
);

const SectionTitle = ({ icon, children }) => (
  <div className="flex items-center gap-2 text-xs font-semibold text-primaryColor uppercase tracking-widest mb-4">
    <span>{icon}</span>
    {children}
  </div>
);

const Divider = () => <hr className="border-t border-gray-100 my-4" />;

const Field = ({ label, value }) => (
  <div>
    <label className="text-xs text-secondaryTextColor block mb-1">{label}</label>
    <div className="text-sm font-semibold text-primaryTextColor">{value || "—"}</div>
  </div>
);

const StatBox = ({ label, value }) => (
  <div className="bg-gray-50 rounded-xl px-3.5 py-2.5">
    <div className="text-[11px] text-secondaryTextColor mb-1">{label}</div>
    <div className="text-sm font-bold text-primaryTextColor">{value}</div>
  </div>
);

const AgreedBadge = ({ val }) => {
  if (val === "Yes")
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#EAF3DE] text-[#3B6D11] flex-shrink-0">
        ✓ Agreed
      </span>
    );
  if (val === "No")
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#FCEBEB] text-[#A32D2D] flex-shrink-0">
        ✕ Disagreed
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#FAEEDA] text-[#854F0B] flex-shrink-0">
      ~ Not sure
    </span>
  );
};

export default PostOrderViaOffer;